import { z } from 'zod';
import {
  ACTIVITY_STATUSES, FORM_ZONES, HR_ZONE_MODELS, isWeekStart, LOAD_METHODS, NO_LOAD_REASONS, PLAN_DURATION_S, PLAN_NOTE_MAX, PLAN_TYPES, PLAUSIBLE_MAX_HR,
  formatPace, SOURCES, SPORT_KINDS, SPORTS, THRESHOLD_KINDS,
  THRESHOLD_SOURCES, THRESHOLD_SPORTS, type ActivityStatus, type FitnessDay, type FormZone, type HrZoneModel, type LoadMethod, type MaxHrStep, type NoLoadReason, type PlanType, type Source,
  type Sport, type Streams, type ThresholdKind, type ThresholdSource, type ThresholdSport,
} from '@painpal/core';

export type { ActivityStatus, FitnessDay, FormZone, HrZoneModel, LoadMethod, MaxHrStep, NoLoadReason, PlanType, Source, Sport, ThresholdKind, ThresholdSource, ThresholdSport };

export const MAX_UPLOAD_MB = 50;
export const MAX_UPLOAD_BYTES = MAX_UPLOAD_MB * 1024 * 1024;
export const MAX_FILES_PER_UPLOAD = 50;

const localDate = z.iso.date();
const instant = z.iso.datetime({ offset: true });
const nullableNumber = z.number().nullable();

export const sportSchema = z.enum(SPORTS);
export const sourceSchema = z.enum(SOURCES);
export const noLoadReasonSchema = z.enum(NO_LOAD_REASONS);
export const formZoneSchema = z.enum(FORM_ZONES);
export const activityStatusSchema = z.enum(ACTIVITY_STATUSES);
export const loadMethodSchema = z.enum(LOAD_METHODS);
export const thresholdKindSchema = z.enum(THRESHOLD_KINDS);

export const activitySummarySchema = z.object({
  id: z.string(),
  source: sourceSchema,
  sport: sportSchema,
  name: z.string(),
  startTime: instant,
  localDate,
  durationS: z.number(),
  movingS: z.number(),
  distanceM: nullableNumber,
  weightedPower: nullableNumber,
  load: nullableNumber,
  noLoadReason: noLoadReasonSchema.nullable(),
  status: activityStatusSchema,
  error: z.string().nullable(),
  // Defaults keep a new web bundle working against an API from before sports (mid-deploy).
  loadMethod: loadMethodSchema.nullable().default(null),
  // Runs: weighted grade-adjusted speed in m/s, shown as grade-adjusted pace.
  weightedSpeed: nullableNumber.default(null),
  avgHr: nullableNumber.default(null),
  // Strava's sport_type, or FIT "<sport>/<subSport>".
  sourceSport: z.string().nullable().default(null),
  stravaUrl: z.string().nullable().default(null),
});
export type ActivitySummary = z.infer<typeof activitySummarySchema>;

export const activityPageSchema = z.object({
  items: z.array(activitySummarySchema),
  nextCursor: z.string().nullable(),
  total: z.number().int(),
  withLoad: z.number().int(),
});
export type ActivityPage = z.infer<typeof activityPageSchema>;

export const zoneSchema = z.object({
  bounds: z.array(z.number().int()),
  seconds: z.array(z.number().int()),
  threshold: z.number(),
  thresholdFrom: localDate,
});
export type Zone = z.infer<typeof zoneSchema>;

export const activityZonesSchema = z.object({ power: zoneSchema.optional(), hr: zoneSchema.optional(), pace: zoneSchema.optional() });
export type ActivityZones = z.infer<typeof activityZonesSchema>;

// The threshold the Load was worked out against: FTP, threshold pace (s/km) or that sport's threshold HR.
export const thresholdUsedSchema = z.object({ kind: thresholdKindSchema, value: z.number(), effectiveFrom: localDate });
export type ThresholdUsed = z.infer<typeof thresholdUsedSchema>;

export const activityDetailSchema = activitySummarySchema.extend({
  elevationM: nullableNumber,
  maxHr: nullableNumber,
  avgPower: nullableNumber,
  intensity: nullableNumber,
  thresholdUsed: thresholdUsedSchema.nullable().default(null),
  streamsUrl: z.string().nullable(),
  zones: activityZonesSchema,
});
export type ActivityDetail = z.infer<typeof activityDetailSchema>;

const series = z.array(z.number().nullable()).optional();
export const streamsPayloadSchema = z.object({
  t: z.array(z.number()),
  power: series,
  hr: series,
  speed: series,
  cadence: series,
  altitude: series,
  lat: series,
  lng: series,
});
export type StreamsPayload = Streams;

export const thresholdSourceSchema = z.enum(THRESHOLD_SOURCES);
export const thresholdSportSchema = z.enum(THRESHOLD_SPORTS);

export const THRESHOLD_RANGES: Record<ThresholdKind, { min: number; max: number }> = {
  ftp: { min: 50, max: 2000 },
  lthr: { min: 80, max: 220 },
  max_hr: { min: 100, max: 240 },
  threshold_pace: { min: 150, max: 900 },
};
const THRESHOLD_LABELS: Record<ThresholdKind, string> = { ftp: 'FTP', lthr: 'LTHR', max_hr: 'MAX HR', threshold_pace: 'THRESHOLD PACE' };

const rangeText = (kind: ThresholdKind, n: number): string => (kind === 'threshold_pace' ? formatPace(n) : String(n));

export function thresholdRangeError(kind: ThresholdKind, value: number): string | null {
  const { min, max } = THRESHOLD_RANGES[kind];
  if (value >= min && value <= max) return null;
  const unit = kind === 'threshold_pace' ? ' /KM' : '';
  return `${THRESHOLD_LABELS[kind]} must be between ${rangeText(kind, min)} and ${rangeText(kind, max)}${unit}`;
}

export const thresholdSchema = z.object({
  id: z.string(),
  sport: thresholdSportSchema,
  kind: thresholdKindSchema,
  value: z.number(),
  effectiveFrom: localDate,
  ridesCovered: z.number().int(),
  // The sports of the activities it covers, in SPORTS order; empty from an API before sports (mid-deploy).
  sportsCovered: z.array(sportSchema).default([]),
  // Defaults keep a new web bundle working against an API from before threshold sources (mid-deploy).
  source: thresholdSourceSchema.default('manual'),
  sourceName: z.string().nullable().default(null),
});
export type Threshold = z.infer<typeof thresholdSchema>;

export const thresholdInputSchema = z.object({
  value: z.number().int(),
  effectiveFrom: localDate,
});
export type ThresholdInput = z.infer<typeof thresholdInputSchema>;

const thresholdCreateBase = thresholdInputSchema.extend({
  sport: thresholdSportSchema.default('ride'),
  kind: thresholdKindSchema.default('ftp'),
});
// Piped so the sport/kind check only runs once every field has parsed; a refine would see a bad sport.
export const thresholdCreateSchema = thresholdCreateBase.pipe(
  z.custom<z.output<typeof thresholdCreateBase>>().superRefine((input, ctx) => {
    if (!SPORT_KINDS[input.sport].includes(input.kind)) {
      ctx.addIssue({ code: 'custom', message: `${input.sport.toUpperCase()} has no ${THRESHOLD_LABELS[input.kind]} threshold`, path: ['kind'] });
      return;
    }
    const message = thresholdRangeError(input.kind, input.value);
    if (message) ctx.addIssue({ code: 'custom', message, path: ['value'] });
  }),
);
export type ThresholdCreate = z.input<typeof thresholdCreateSchema>;

export const thresholdListQuerySchema = z.object({ sport: thresholdSportSchema.default('ride'), kind: thresholdKindSchema.default('ftp') });

export const thresholdListSchema = z.object({
  items: z.array(thresholdSchema),
  recalculating: z.object({ rides: z.number().int(), fromDate: localDate }).nullable(),
});
export type ThresholdList = z.infer<typeof thresholdListSchema>;

export const ftpSuggestionSchema = z.discriminatedUnion('state', [
  z.object({ state: z.literal('none') }),
  z.object({ state: z.literal('reconnect') }),
  // yours is null when there is no FTP entry at all.
  z.object({ state: z.literal('suggest'), strava: z.number().int(), yours: z.number().int().nullable() }),
]);

export const SPORT_SUGGESTION_ACTIONS = ['run-pace', 'run-lthr', 'other-lthr'] as const;
export type SportSuggestionAction = (typeof SPORT_SUGGESTION_ACTIONS)[number];

// from: 'runs' for a value worked out from the athlete's runs; 'run' / 'ride' for other sports taking that threshold HR.
// basis is the activity the value rests on, when there is one.
export const sportSuggestionSchema = z.discriminatedUnion('state', [
  z.object({ state: z.literal('none') }),
  z.object({
    state: z.literal('suggest'), value: z.number().int(), yours: z.number().int().nullable(),
    from: z.enum(['runs', 'run', 'ride']), basis: z.object({ name: z.string(), localDate }).nullable(),
  }),
]);
export type SportSuggestion = z.infer<typeof sportSuggestionSchema>;
const NO_SUGGESTION = { state: 'none' } as const;

export const maxHrStepSchema = z.object({ activityId: z.string(), name: z.string(), localDate, value: z.number().int() });

export const thresholdSuggestionsSchema = z.object({
  ftp: ftpSuggestionSchema,
  // Newest first.
  maxHr: z.array(maxHrStepSchema),
  // Defaults keep a new web bundle working against an API from before sports (mid-deploy).
  runPace: sportSuggestionSchema.default(NO_SUGGESTION),
  runLthr: sportSuggestionSchema.default(NO_SUGGESTION),
  otherLthr: sportSuggestionSchema.default(NO_SUGGESTION),
  // This read applied a first Max HR or sport threshold history, so the client refetches its thresholds.
  autoApplied: z.boolean(),
});
export type ThresholdSuggestions = z.infer<typeof thresholdSuggestionsSchema>;

export const maxHrAcceptSchema = z.object({ activityIds: z.array(z.uuid()).min(1, 'Tick at least one step') });
export type MaxHrAccept = z.infer<typeof maxHrAcceptSchema>;

// Dismissals carry what the athlete saw, so a value or step that arrived after the card loaded stays offered.
export const ftpDismissSchema = z.object({
  strava: z.number().int().min(THRESHOLD_RANGES.ftp.min).max(THRESHOLD_RANGES.ftp.max),
});
export type FtpDismiss = z.infer<typeof ftpDismissSchema>;
// Each step beats the one before it by at least 1 bpm inside PLAUSIBLE_MAX_HR, so no history has more steps than this.
const MAX_HR_STEPS_MAX = PLAUSIBLE_MAX_HR.max - PLAUSIBLE_MAX_HR.min + 1;
export const maxHrDismissSchema = z.object({ activityIds: z.array(z.uuid()).min(1).max(MAX_HR_STEPS_MAX) });
export type MaxHrDismiss = z.infer<typeof maxHrDismissSchema>;
// Pace in s/km and threshold HR in bpm share one plausible span.
export const suggestionDismissSchema = z.object({ value: z.number().int().min(THRESHOLD_RANGES.lthr.min).max(THRESHOLD_RANGES.threshold_pace.max) });
export type SuggestionDismiss = z.infer<typeof suggestionDismissSchema>;

export const fitnessDaySchema = z.object({
  date: localDate,
  load: z.number(),
  fitness: z.number(),
  fatigue: z.number(),
  form: z.number(),
});

// A stored day, with its Load split by sport (sports with Load that day only).
export const fitnessHistoryDaySchema = fitnessDaySchema.extend({ loadBySport: z.partialRecord(sportSchema, z.number()).default({}) });
export type FitnessHistoryDay = z.infer<typeof fitnessHistoryDaySchema>;

export const fitnessResponseSchema = z.object({
  days: z.array(fitnessHistoryDaySchema),
  // Every sport with a counted activity, in SPORTS order, for the sport filter.
  sports: z.array(sportSchema).default([]),
  ridesMissingFtp: z.number().int(),
  // True while a rebuild of the stored Fitness series is queued or running, so the days may be stale.
  // Defaults to false so a new web bundle tolerates an API that predates the flag during a deploy.
  rebuilding: z.boolean().default(false),
  // First day of the user's stored history, however far back `days` reaches. Null (also mid-deploy) means unknown.
  historyStart: localDate.nullable().default(null),
  // Planned days ahead, from today (or tomorrow once today has a ride) to the last planned day, at most 42 days out.
  // Empty with no plan after today, and (mid-deploy) from an API before planning.
  forecast: z.array(fitnessDaySchema).default([]),
  // How many planned days (Rest included) fall on the forecast's dates.
  forecastPlannedDays: z.number().int().default(0),
});
export type FitnessResponse = z.infer<typeof fitnessResponseSchema>;

export const stravaStatusSchema = z.object({
  connected: z.boolean(),
  // unreadable: PainPal couldn't read its stored Strava access; reconnecting fixes it, and nothing is deleted.
  status: z.enum(['active', 'revoked', 'unreadable']).nullable(),
  athleteId: z.string().nullable(),
  backfill: z
    .object({
      status: z.enum(['idle', 'running', 'done']),
      done: z.number().int(),
      total: z.number().int().nullable(),
      // Whether rides are still landing, which goes on after the listing ends ('done'). Missing from an API from before
      // it (mid-deploy).
      importing: z.boolean().optional(),
    })
    .nullable(),
  lastActivityAt: instant.nullable(),
  athleteName: z.string().nullable(),
  lastSyncAt: instant.nullable(),
  // Strava's FTP from the last sync; defaults to null for an API from before it (mid-deploy).
  ftp: z.number().int().nullable().default(null),
  // Activities imported from Strava, which disconnecting deletes; 0 from an API from before it (mid-deploy).
  activities: z.number().int().default(0),
});
export type StravaStatus = z.infer<typeof stravaStatusSchema>;

export const uploadRequestSchema = z.object({
  files: z
    .array(
      z.object({
        name: z.string().regex(/\.fit$/i, 'Only .fit files can be uploaded'),
        size: z.number().int().positive().max(MAX_UPLOAD_BYTES, `Files must be ${MAX_UPLOAD_MB} MB or smaller`),
      }),
    )
    .min(1)
    .max(MAX_FILES_PER_UPLOAD),
});
export type UploadRequest = z.infer<typeof uploadRequestSchema>;

export const uploadTicketSchema = z.object({ name: z.string(), key: z.string(), uploadUrl: z.string() });
export type UploadTicket = z.infer<typeof uploadTicketSchema>;

export const uploadResponseSchema = z.object({ uploads: z.array(uploadTicketSchema) });
export type UploadResponse = z.infer<typeof uploadResponseSchema>;

export const uploadStatusSchema = z.object({
  key: z.string(),
  status: activityStatusSchema,
  activityId: z.string().nullable(),
  error: z.string().nullable(),
});
export type UploadStatus = z.infer<typeof uploadStatusSchema>;

export const apiErrorSchema = z.object({ error: z.object({ code: z.string(), message: z.string() }) });
export type ApiError = z.infer<typeof apiErrorSchema>;

export const STRAVA_RETURN_PATHS = ['/settings', '/onboarding/rides'] as const;
export type StravaReturnPath = (typeof STRAVA_RETURN_PATHS)[number];

export const stravaConnectRequestSchema = z.object({
  returnTo: z.enum(STRAVA_RETURN_PATHS).default('/settings'),
});
export type StravaConnectRequest = z.input<typeof stravaConnectRequestSchema>;

export const hrZoneModelSchema = z.enum(HR_ZONE_MODELS);
export const settingsSchema = z.object({ hrZoneModel: hrZoneModelSchema });
export type Settings = z.infer<typeof settingsSchema>;

export const powerCurveWindowSchema = z.enum(['90d', 'all']);
export const powerCurveComparisonSchema = z.object({
  durations: z.array(z.number().int()),
  ride: z.array(z.number()),
  best: z.array(z.number().nullable()),
  window: powerCurveWindowSchema,
});
export type PowerCurveComparison = z.infer<typeof powerCurveComparisonSchema>;

export const zoneSourceSchema = z.enum(['power', 'hr', 'pace']);
export type ZoneSource = z.infer<typeof zoneSourceSchema>;

export const weeklyZonesSchema = z.object({
  weeks: z.array(z.object({ weekStart: localDate, seconds: z.array(z.number().int()) })),
  bounds: z.array(z.number().int()).optional(),
});
export type WeeklyZones = z.infer<typeof weeklyZonesSchema>;

export const fitnessValuesSchema = z.object({ fitness: z.number(), fatigue: z.number(), form: z.number() });
export type FitnessValues = z.infer<typeof fitnessValuesSchema>;

export const sportFiguresSchema = z.object({ load: z.number(), distanceM: z.number(), movingS: z.number() });

// Every counted activity that day (of the requested sport, when filtered), how many had no Load, and each sport's share.
// Defaults keep a new web bundle working against an API from before sports (mid-deploy).
export const weekDaySchema = z.object({
  date: localDate,
  distanceM: z.number(),
  movingS: z.number(),
  load: z.number(),
  activities: z.number().int().default(0),
  noLoad: z.number().int().default(0),
  bySport: z.partialRecord(sportSchema, sportFiguresSchema).default({}),
});
export type WeekDay = z.infer<typeof weekDaySchema>;

// One Monday-to-Sunday week, zero-filled. `end` is the Sunday's values (today's for the current week) and `previousEnd`
// the Sunday before; each is null outside the stored history or after today.
export const weekSnapshotSchema = z.object({
  start: localDate,
  days: z.array(weekDaySchema).length(7),
  end: fitnessValuesSchema.nullable(),
  previousEnd: fitnessValuesSchema.nullable(),
  historyStart: localDate.nullable(),
});
export type WeekSnapshot = z.infer<typeof weekSnapshotSchema>;

export const planTypeSchema = z.enum(PLAN_TYPES);

// pipe, not refine: zod 4 runs a refine even after the date format check fails, and weekStart throws on non-dates.
export const weekStartSchema = localDate.pipe(z.string().refine(isWeekStart, 'Weeks start on a Monday'));

const durationMessage = 'Duration must be between 0:05 and 12:00';
export const planInputSchema = z.object({
  type: planTypeSchema,
  durationS: z.number().int(durationMessage).min(PLAN_DURATION_S.min, durationMessage).max(PLAN_DURATION_S.max, durationMessage).nullable().optional(),
  note: z.string().trim().max(PLAN_NOTE_MAX, `Notes are ${PLAN_NOTE_MAX} characters or fewer`).nullable().optional(),
});
export type PlanInput = z.infer<typeof planInputSchema>;

export const planSchema = z.object({
  type: planTypeSchema,
  durationS: z.number().int().nullable(),
  note: z.string().nullable(),
  // From the type's default duration when durationS is null (defaulted); always 0 for Rest.
  estimatedLoad: z.number(),
  defaulted: z.boolean(),
});
export type Plan = z.infer<typeof planSchema>;

// Every counted activity that local day; rides counts sport `ride` only, which is what makes a planned day done.
// ride is the day's first such ride by start time, for the done strip's link; null without one.
export const riddenSchema = z.object({
  movingS: z.number(), load: z.number(), rides: z.number().int(), ride: z.object({ id: z.string(), name: z.string() }).nullable(),
});
export type Ridden = z.infer<typeof riddenSchema>;

export const planDaySchema = z.object({
  date: localDate,
  plan: planSchema.nullable(),
  ridden: riddenSchema,
  status: z.enum(['done', 'missed']).nullable(),
});
export type PlanDay = z.infer<typeof planDaySchema>;

export const PLAN_RANGE_MAX_DAYS = 42;
// today is the profile zone's, the day statuses were judged against; the browser's can differ by a day.
export const planRangeSchema = z.object({ today: localDate, days: z.array(planDaySchema) });
export type PlanRange = z.infer<typeof planRangeSchema>;

export const planCopySchema = z.object({ from: weekStartSchema, to: weekStartSchema });
export type PlanCopy = z.infer<typeof planCopySchema>;

// consentedAt: when the athlete agreed to PainPal processing their heart rate and activity data; null until they have.
// Absent from an API from before consent (mid-deploy), which can't record it: the web then neither asks nor gates.
export const accountInfoSchema = z.object({ hasPassword: z.boolean(), consentedAt: instant.nullable().optional() });
export type AccountInfo = z.infer<typeof accountInfoSchema>;

export const setPasswordRequestSchema = z.object({
  email: z.string().trim().toLowerCase().pipe(z.email('Enter a valid email address')),
  newPassword: z.string().min(8, 'Use at least 8 characters').max(128, 'Use 128 characters or fewer'),
});
export type SetPasswordRequest = z.infer<typeof setPasswordRequestSchema>;
